import type { PaginatedResponse } from '@/lib/api/params'
import type { Platform } from '@/types/market'
import type { FilterConfig, SortConfig } from './types'
import { isPlatformEnabled } from './types'

const PAGE_SIZE = 50
const MIN_BUFFER = 10

type PlatformState<T> = {
	buffer: T[]
	offset: number
	hasMore: boolean
	isLoading: boolean
}

type StreamConfig<T> = {
	platforms: Platform[]
	endpoint: 'events' | 'markets'
	compare: (a: T, b: T, sort: SortConfig) => number
}

export class MergedStream<T extends { platform: Platform; id: string }> {
	private platformStates: Map<Platform, PlatformState<T>>
	private sort: SortConfig
	private filters: FilterConfig
	private yielded: Set<string> = new Set()
	private config: StreamConfig<T>

	constructor(config: StreamConfig<T>, sort: SortConfig, filters: FilterConfig = {}) {
		this.config = config
		this.sort = sort
		this.filters = filters
		this.platformStates = new Map()

		for (const platform of config.platforms) {
			this.platformStates.set(platform, {
				buffer: [],
				offset: 0,
				hasMore: isPlatformEnabled(platform, filters),
				isLoading: false,
			})
		}
	}

	private buildParams(offset: number): URLSearchParams {
		const params = new URLSearchParams()
		params.set('limit', PAGE_SIZE.toString())
		params.set('offset', offset.toString())
		params.set('sort', this.sort.key)

		if (this.filters.search) {
			params.set('search', this.filters.search)
		}
		if (this.filters.categories?.length) {
			params.set('categories', this.filters.categories.join(','))
		}
		if (this.filters.activeOnly === true) {
			params.set('status', 'active')
		}
		if (this.filters.minVolume !== undefined) {
			params.set('minVolume', this.filters.minVolume.toString())
		}
		if (this.filters.maxVolume !== undefined) {
			params.set('maxVolume', this.filters.maxVolume.toString())
		}
		if (this.filters.minLiquidity !== undefined) {
			params.set('minLiquidity', this.filters.minLiquidity.toString())
		}
		if (this.filters.maxLiquidity !== undefined) {
			params.set('maxLiquidity', this.filters.maxLiquidity.toString())
		}

		return params
	}

	private buildPlatformParams(platform: Platform, offset: number): URLSearchParams {
		const params = this.buildParams(offset)

		// Kalshi-specific: pass isInitialized=true when filtering for tradable markets
		if (platform === 'kalshi' && this.filters.tradableOnly) {
			params.set('isInitialized', 'true')
		}

		return params
	}

	private async fetchPage(platform: Platform): Promise<void> {
		const state = this.platformStates.get(platform)
		if (!state || state.isLoading || !state.hasMore) {
			return
		}

		state.isLoading = true
		try {
			const params = this.buildPlatformParams(platform, state.offset)
			const res = await fetch(`/api/${platform}/${this.config.endpoint}?${params.toString()}`)
			if (!res.ok) {
				throw new Error(`${platform} fetch failed: ${res.status}`)
			}

			const data: PaginatedResponse<T> = await res.json()

			state.buffer.push(...data.data)
			state.offset = data.pagination.nextOffset ?? state.offset
			state.hasMore = data.pagination.hasMore
		} catch (error) {
			console.error(`${platform} ${this.config.endpoint} fetch error:`, error)
			state.hasMore = false
		} finally {
			state.isLoading = false
		}
	}

	private async ensureBuffers(): Promise<void> {
		const fetches: Promise<void>[] = []

		for (const [platform, state] of this.platformStates) {
			if (state.buffer.length < MIN_BUFFER && state.hasMore && !state.isLoading) {
				fetches.push(this.fetchPage(platform))
			}
		}

		if (fetches.length > 0) {
			await Promise.all(fetches)
		}
	}

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Merge algorithm requires handling multiple platform buffers
	async getNext(count: number): Promise<T[]> {
		const result: T[] = []

		while (result.length < count) {
			await this.ensureBuffers()

			// Collect all available heads with their sources
			const candidates: { item: T; state: PlatformState<T> }[] = []
			for (const state of this.platformStates.values()) {
				if (state.buffer[0]) {
					candidates.push({ item: state.buffer[0], state })
				}
			}

			if (candidates.length === 0) {
				break
			}

			// Find the best candidate using compare
			let best = candidates[0]
			for (let i = 1; i < candidates.length; i++) {
				const cmp = this.config.compare(best.item, candidates[i].item, this.sort)
				if (cmp > 0) {
					best = candidates[i]
				}
			}

			const chosen = best.state.buffer.shift()
			if (!chosen) {
				break
			}

			const key = `${chosen.platform}:${chosen.id}`
			if (!this.yielded.has(key)) {
				this.yielded.add(key)
				result.push(chosen)
			}
		}

		return result
	}

	hasMore(): boolean {
		for (const state of this.platformStates.values()) {
			if (state.buffer.length > 0 || state.hasMore) {
				return true
			}
		}
		return false
	}

	isLoading(): boolean {
		for (const state of this.platformStates.values()) {
			if (state.isLoading) {
				return true
			}
		}
		return false
	}

	reset(sort?: SortConfig, filters?: FilterConfig): void {
		if (sort) {
			this.sort = sort
		}
		if (filters) {
			this.filters = filters
		}

		for (const platform of this.config.platforms) {
			this.platformStates.set(platform, {
				buffer: [],
				offset: 0,
				hasMore: isPlatformEnabled(platform, this.filters),
				isLoading: false,
			})
		}

		this.yielded.clear()
	}
}
